import * as React from 'react';
import { Dispatch } from 'react-redux';
import { ContextualMenuItemType } from '../../constants';
import { StoreState } from '../../../../reducers';
import { DriveItemInfo } from '../../../../containers/DriveItemList/reducer';
import { StyleColor, NewStyleColor } from '../../../../utils/styles-constants';
import {
	closeContextualMenu,
	deleteFromAsp,
	archiveProposal,
	getOrFetchDriveItemMeta,
	viewDriveItem,
	DriveItemOrigin,
	fetchDriveItem,
	lockContextualMenu,
	unlockContextualMenu,
} from '../../../../containers/DriveItemList/actions';
import { deleteItem, requestWebDownloadItem, requestMobileAppDownload } from '../../../../containers/DownloadManager/actions';
import UserAgentUtils from '../../../../utils/userAgent';
import StatsUtils from '../../../../utils/statistics';
import ContextualMenuItem, { ContextualMenuItemProps } from '..';
import { share } from '../../../../containers/Share/actions';
import { getShareUsers } from '../../../../containers/ShareWithUserPopup/actions';
import RoutesUtils from '../../../../utils/routes';
import { openPublicationCreatorForSaveAs, setAsTemplate, removeTemplate } from '../../../../containers/PublicationCreator/actions';
import { openAssetRenamePanel } from '../../../../containers/AssetRenamePanel/actions';
import { openNoSaveAsPopup } from '../../../../containers/NoSaveAsPopup/actions';
import { openCoverImagePanel } from '../../../../containers/CoverImagePanel/actions';
import { downloadThumb } from '../../../../containers/OfflineManager/actions';
import { DownloadPriority } from '../../../../utils/nativeApi';
import { Api } from '../../../../typings/api';
import {
	addSelectedDownloadItem, startDownloadGroup,
} from '../../../../containers/DownloadManager/actions/store';
import { downloadSelectedAssets, updateDriveItemWithDependencies } from '../../../../containers/DownloadManager/actions/download';
import { DownloadGroupName, DownloadMode } from '../../../../containers/DownloadManager/reducer';
import ApiUtils from '../../../../utils/api';

interface ContextualMenuItemContainerProps {
	dispatch: Dispatch<StoreState>;
	driveItem: DriveItemInfo;
	type: ContextualMenuItemType;
	clientId: number;
	isTemplate: boolean;
	isDownloadAllPendingSelectedItems: boolean;
	isDownloaded: boolean;
	driveItems: { [key: number]: DriveItemInfo };
	privateDir: Api.DriveItem;
}

class ContextualMenuItemContainer extends React.Component<ContextualMenuItemContainerProps, never> {
	constructor(props: ContextualMenuItemContainerProps) {
		super(props);
	}

	close() {
		const { dispatch } = this.props;
		dispatch(closeContextualMenu());
	}

	onDownloadClick() {
		const { driveItem, dispatch, isDownloadAllPendingSelectedItems } = this.props;
		const { isMobileApp } = UserAgentUtils;

		if (driveItem) {
			StatsUtils.trackDownload([driveItem]);

			if (!isMobileApp) {
				dispatch(requestWebDownloadItem(driveItem));
			} else {
				dispatch(addSelectedDownloadItem(driveItem.id));

				if (!isDownloadAllPendingSelectedItems) {
					StatsUtils.trackDownloadSelected();
					dispatch(startDownloadGroup(DownloadGroupName.SELECTED));
					dispatch(requestMobileAppDownload(driveItem, DownloadMode.Requested, undefined, true));
				} else {
					dispatch(downloadSelectedAssets());
				}
			}
		}
		this.close();
	}

	onUpdateClick() {
		const {driveItem, dispatch, isDownloaded} = this.props;
		const {isMobileApp} = UserAgentUtils;

		if (driveItem) {
			if (!isMobileApp) {
				dispatch(requestWebDownloadItem(driveItem));
			} else {
				if (isDownloaded) {
					dispatch(requestMobileAppDownload(driveItem, DownloadMode.Requested, undefined, true));
				} else {
					dispatch(requestMobileAppDownload(driveItem, DownloadMode.Requested));
				}
			}
		}
		this.close();
	}

	onDeleteClick() {
		const { driveItem, dispatch } = this.props;

		dispatch(deleteItem(driveItem));
		this.close();
	}

	onShareClick() {
		const { dispatch, driveItem } = this.props;

		dispatch(share([driveItem]));
		this.close();
	}

	onShareWithUserClick() {
		const { dispatch, driveItem: { id } } = this.props;

		dispatch(getShareUsers(id));
		this.close();
	}

	onEditClick() {
		const { clientId, driveItem: { id } } = this.props;
		RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, id));
		this.close();
	}

	onProposalNoSaveAs() {
		const { dispatch } = this.props;
		dispatch(openNoSaveAsPopup());
		this.close();
	}

	onPublicationCopy() {
		const { driveItem, dispatch } = this.props;
		dispatch(openPublicationCreatorForSaveAs(driveItem));
		this.close();
	}

	onRename() {
		const { driveItem, dispatch } = this.props;
		dispatch(openAssetRenamePanel(driveItem));
		this.close();
	}

	onEditCoverImageClick() {
		const { driveItem, dispatch } = this.props;

		dispatch(openCoverImagePanel(driveItem));
		this.close();
	}

	onDeleteFromAspClick() {
		const { driveItem, clientId, dispatch } = this.props;

		dispatch(deleteFromAsp(clientId, driveItem));
		this.close();
	}

	onSetAsTemplateClick() {
		const { driveItem: { id }, clientId, dispatch } = this.props;

		dispatch(setAsTemplate(clientId, id));
		this.close();
	}

	onRemoveTemplateClick() {
		const { driveItem: { id }, clientId, dispatch } = this.props;

		dispatch(removeTemplate(clientId, id));
		this.close();
	}

	onArchiveClick() {
		const { driveItem, clientId, dispatch } = this.props;

		dispatch(archiveProposal(clientId, driveItem));
		this.close();
	}

	onCheckForUpdateClick() {
		const { driveItem } = this.props;
		this.updateMetaAndThumbnail(driveItem).then();
		this.close();
	}

	formatCurrentDate() {
		const separators = ['/', '/', '', ':', ':'];
		let separatorIndex = 0;
		return new Date().toLocaleString('en-GB', {
			day: '2-digit',
			month: '2-digit',
			year: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		}).replace(/,/g, (match, offset) => separators[separatorIndex++]);
	}

	async createProposal(mustOpenEditor: boolean) {
		const {
			driveItem: {
				id,
				label,
			},
			clientId,
			privateDir,
			dispatch,
		} = this.props;
		dispatch(lockContextualMenu());
		try {

			const requestId = 'create-proposal';
			const formattedDate = this.formatCurrentDate();

			const { globalId } = await ApiUtils.createProposalFromDriveItem(
				privateDir.id,
				clientId,
				id,
				`${label}-${formattedDate}`,
				requestId
			);
			await dispatch(getOrFetchDriveItemMeta(privateDir.id, true, true, true));
			await dispatch(fetchDriveItem(globalId, privateDir.id, true));
			dispatch(viewDriveItem(privateDir, DriveItemOrigin.List));

			if (mustOpenEditor) {
				RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, globalId));
			}

			dispatch(unlockContextualMenu());
			this.close();
		} catch (error) {
			dispatch(unlockContextualMenu());
		}
	}

	onCreateProposalClick() {
		this.createProposal(false);
	}

	onCreateCustomizedProposalClick() {
		this.createProposal(true);
	}

	async updateMetaAndThumbnail(driveItem: Api.DriveItem) {
		const { dispatch, isDownloaded } = this.props;

		try {
			const meta = await dispatch(getOrFetchDriveItemMeta(driveItem.id, true, true, true)) as Api.DriveItem;
			if (meta.id) {
				dispatch(downloadThumb(driveItem.id, DownloadPriority.High, false, meta, true));
			}

			if (isDownloaded) {
				dispatch(updateDriveItemWithDependencies(driveItem, true)).then();
			}
		} catch (error) {
			console.error('Failed to update meta and thumbnail:', error);
		}
	}

	getItemAttributes(type: ContextualMenuItemType): ContextualMenuItemProps {
		const defaultSize = 20;
		switch (type) {
			case ContextualMenuItemType.Download:
				return {
					label: UserAgentUtils.isMobileApp ? 'messages.activate_offline_mode' : 'messages.download',
					iconName: UserAgentUtils.isMobileApp ? 'iconset_uprez_check_circle' : 'iconset_uprez_download_bold',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onDownloadClick(),
					className: ContextualMenuItemType.Download,
				};
			case ContextualMenuItemType.Update:
				return {
					label: 'messages.update',
					iconName: 'iconset_update',
					color: StyleColor.Blue600,
					size: defaultSize + 2,
					onClick: () => this.onUpdateClick(),
					className: ContextualMenuItemType.Update,
				};
			case ContextualMenuItemType.Delete:
				return {
					label: 'messages.available_offline',
					iconName: 'iconset_uprez_check_circle_green',
					color: NewStyleColor.VibrantGreen,
					size: defaultSize + 2,
					onClick: () => this.onDeleteClick(),
					className: ContextualMenuItemType.Delete,
					hoverLabel: 'messages.deactivate_offline_mode',
					hoverIconName: 'iconset_delete',
				};
			case ContextualMenuItemType.Share:
				return {
					label: 'messages.share',
					iconName: 'iconset_uprez_share_bold',
					color: StyleColor.Black,
					size: defaultSize,
					onClick: () => this.onShareClick(),
					className: ContextualMenuItemType.Share,
				};
			case ContextualMenuItemType.ShareWithUser:
				return {
					label: 'messages.invite_coworker',
					iconName: 'iconset_uprez_invite',
					color: StyleColor.Black,
					size: defaultSize + 2,
					onClick: () => this.onShareWithUserClick(),
					className: ContextualMenuItemType.ShareWithUser,
				};
			case ContextualMenuItemType.Edit:
				return {
					label: 'messages.edit',
					iconName: 'iconset_uprez_write',
					color: StyleColor.Black,
					size: defaultSize,
					onClick: () => this.onEditClick(),
					className: ContextualMenuItemType.Edit,
				};
			case ContextualMenuItemType.ProposalNoSaveAs:
				return {
					label: 'messages.save_as',
					iconName: 'iconset_uprez_info',
					color: StyleColor.Red,
					size: defaultSize + 2,
					onClick: () => this.onProposalNoSaveAs(),
					className: ContextualMenuItemType.ProposalNoSaveAs,
				};
			case ContextualMenuItemType.PublicationSaveAs:
				return {
					label: 'messages.save_as',
					iconName: 'iconset_uprez_save_as',
					color: StyleColor.Black,
					size: defaultSize,
					onClick: () => this.onPublicationCopy(),
					className: ContextualMenuItemType.PublicationSaveAs,
				};
			case ContextualMenuItemType.Rename:
				return {
					label: 'messages.rename',
					iconName: 'iconset_uprez_rename',
					color: NewStyleColor.Black,
					size: defaultSize + 2,
					onClick: () => this.onRename(),
					className: ContextualMenuItemType.Rename,
				};
			case ContextualMenuItemType.EditCoverImage:
				return {
					label: 'messages.change_cover_image',
					iconName: 'iconset_uprez_cover_image',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onEditCoverImageClick(),
					className: ContextualMenuItemType.EditCoverImage,
				};
			case ContextualMenuItemType.DeleteFromAsp:
				return {
					label: 'messages.delete',
					iconName: 'iconset_uprez_delete',
					color: NewStyleColor.Black,
					size: defaultSize + 1,
					onClick: () => this.onDeleteFromAspClick(),
					className: ContextualMenuItemType.DeleteFromAsp,
				};
			case ContextualMenuItemType.SetAsTemplate:
				const { isTemplate } = this.props;
				return {
					label: isTemplate ? 'messages.available_as_template' : 'messages.set_as_template',
					iconName: isTemplate ? 'iconset_uprez_check_circle_green' : 'iconset_uprez_set_as_template',
					color: isTemplate ? NewStyleColor.VibrantGreen : NewStyleColor.Black,
					size: isTemplate ? defaultSize + 2 : defaultSize,
					onClick: () => {
						if (isTemplate) {
							this.onRemoveTemplateClick();
						} else {
							this.onSetAsTemplateClick();
						}
					},
					className: ContextualMenuItemType.SetAsTemplate,
				};
			case ContextualMenuItemType.RemoveTemplate:
				return {
					label: 'messages.remove_template',
					iconName: 'iconset_uprez_delete',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onRemoveTemplateClick(),
					className: ContextualMenuItemType.RemoveTemplate,
				};
			case ContextualMenuItemType.Archive:
				return {
					label: 'messages.archive_verb',
					iconName: 'iconset_uprez_archive',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onArchiveClick(),
					className: ContextualMenuItemType.Archive,
				};
			case ContextualMenuItemType.CheckForUpdate:
				return {
					label: 'messages.check_for_updates',
					iconName: 'iconset_uprez_check_for_updates',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onCheckForUpdateClick(),
					className: ContextualMenuItemType.CheckForUpdate,
				};
			case ContextualMenuItemType.CreateProposal:
				return {
					label: 'messages.create_proposal',
					iconName: 'iconset_uprez_create_proposal',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onCreateProposalClick(),
					className: ContextualMenuItemType.CreateProposal,
				};
			case ContextualMenuItemType.CreateCustomizedProposal:
				return {
					label: 'messages.create_customized_proposal',
					iconName: 'iconset_uprez_create_customized_proposal',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onCreateCustomizedProposalClick(),
					className: ContextualMenuItemType.CreateProposal,
				};
		}
	}

	render() {
		const { type } = this.props;

		const itemAttributes = this.getItemAttributes(type);
		return <ContextualMenuItem {...itemAttributes} />;
	}
}

export default ContextualMenuItemContainer;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ContextualMenu/Item/Container/index.tsx